import { skipToken } from "@reduxjs/toolkit/query/react";
import { AlertTriangle } from "lucide-react";
import { useGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberQuery } from "randmar-api-client";
import { Alert, AlertDescription, AlertTitle, RmBodyZone, Skeleton } from "randmarcomps";
import * as React from "react";
import { useParams } from "react-router-dom";
import DocumentActionsMenu from "../components/document/DocumentActionsMenu";
import PurchaseOrderView from "../components/document/PurchaseOrderView";
import ReceiptView from "../components/document/ReceiptView";
import RequisitionView from "../components/document/RequisitionView";
import DataRefreshAssistantCluster from "../components/layout/DataRefreshAssistantCluster";
import RmPageHeader from "../components/layout/RmPageHeader";
import { useManufacturer } from "../contexts/WithManufacturer";
import { PurchaseOrder, Receipt, Requisition } from "../stores/document/Document";

const handleDocumentName = (docNum: string) => {
  if (docNum.startsWith("PO") && docNum.length === 9) return "Purchase Order";
  if (docNum.startsWith("RQ") && docNum.length === 9) return "Draft Purchase";
  if (docNum.startsWith("RC") && docNum.length === 9) return "Reception";
  return "Document";
};

type DocumentKind = "purchase-order" | "draft-purchase" | "reception" | "unknown";

const getDocumentKind = (docNum: string): DocumentKind => {
  if (docNum.startsWith("PO") && docNum.length === 9) return "purchase-order";
  if (docNum.startsWith("RQ") && docNum.length === 9) return "draft-purchase";
  if (docNum.startsWith("RC") && docNum.length === 9) return "reception";
  return "unknown";
};

function buildDocumentAssistantPrompt(options: {
  applicationId: string;
  documentNumber: string;
  documentName: string;
  kind: DocumentKind;
  purchaseOrder?: PurchaseOrder | null;
  requisition?: Requisition | null;
  receipt?: Receipt | null;
}) {
  const lines: string[] = [
    "You are assisting with the Randmar Manufacturer Portal Document page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    "",
    `Document: ${options.documentName} (${options.documentNumber})`,
    `Kind: ${options.kind}`,
    "",
  ];

  if (options.kind === "purchase-order" && options.purchaseOrder) {
    const po = options.purchaseOrder;
    lines.push(
      `Vendor: ${po.vendorName ?? "Unknown"}`,
      `Location: ${po.location ?? "Unknown"}`,
      `PurchaseOrderDate: ${po.purchaseOrderDate ?? "Unknown"}`,
      `ExpectedArrivalDate: ${po.expectedArrivalDate ?? "Unknown"}`,
      `Subtotal: ${po.subTotal ?? 0}`,
      `Line items: ${(po.partNumbers ?? []).length}`,
      `Receipts: ${(po.receipts ?? []).length}`,
      ""
    );
  }

  if (options.kind === "draft-purchase" && options.requisition) {
    const rq = options.requisition;
    lines.push(
      `RequestedBy: ${rq.requestedBy ?? "Unknown"}`,
      `Reference: ${rq.reference ?? "None"}`,
      `Location: ${rq.location ?? "Unknown"}`,
      `DraftDate: ${rq.requisitionDate ?? "Unknown"}`,
      `Subtotal: ${rq.subTotal ?? 0}`,
      `Line items: ${(rq.partNumbers ?? []).length}`,
      ""
    );
  }

  if (options.kind === "reception" && options.receipt) {
    const rc = options.receipt;
    lines.push(
      `PurchaseOrderNumber: ${rc.purchaseOrderNumber ?? "Unknown"}`,
      `Location: ${rc.location ?? "Unknown"}`,
      `ReceiptDate: ${rc.receiptDate ?? "Unknown"}`,
      `Invoiced: ${rc.invoiced ?? "Unknown"}`,
      `InvoiceNumber: ${rc.invoiceNumber ?? "None"}`,
      `Total: ${rc.total ?? 0}`,
      `Line items: ${(rc.partNumbers ?? []).length}`,
      ""
    );
  }

  lines.push(
    "Help me understand what this document says, flag anything unusual, and suggest next actions I should take."
  );
  return lines.join("\n");
}

const Document: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { documentNumber = "" } = useParams<{ documentNumber: string }>();
  const documentName = handleDocumentName(documentNumber);
  const documentKind = React.useMemo(() => getDocumentKind(documentNumber), [documentNumber]);

  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const canQuery = Boolean(applicationId && documentNumber);
  const documentQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberQuery(
    canQuery ? { routeManufacturerId: applicationId, documentNumber } : skipToken
  );

  const documentData = documentQuery.data;
  const isLoading = documentQuery.isLoading;
  const isRefreshing = documentQuery.isFetching;

  React.useEffect(() => {
    const previousTitle = window.document.title;
    if (documentNumber) {
      window.document.title = `${documentName} ${documentNumber} | ${previousTitle}`;
    } else {
      window.document.title = `${documentName} | ${previousTitle}`;
    }
    return () => {
      window.document.title = previousTitle;
    };
  }, [documentName, documentNumber]);

  React.useEffect(() => {
    if (!canQuery) {
      setLastRefreshedAt(null);
      return;
    }
    if (!isRefreshing && (documentQuery.isSuccess || documentQuery.isError)) {
      setLastRefreshedAt(new Date());
    }
  }, [canQuery, documentQuery.isError, documentQuery.isSuccess, isRefreshing]);

  const purchaseOrder = React.useMemo(() => {
    if (!documentData || documentKind !== "purchase-order") return null;
    return PurchaseOrder.convertFrom(documentData as unknown);
  }, [documentData, documentKind]);

  const requisition = React.useMemo(() => {
    if (!documentData || documentKind !== "draft-purchase") return null;
    return Requisition.convertFrom(documentData as unknown);
  }, [documentData, documentKind]);

  const receipt = React.useMemo(() => {
    if (!documentData || documentKind !== "reception") return null;
    return Receipt.convertFrom(documentData as unknown);
  }, [documentData, documentKind]);

  const assistantPrompt = React.useMemo(() => {
    if (!applicationId) {
      return "";
    }
    return buildDocumentAssistantPrompt({
      applicationId,
      documentNumber,
      documentName,
      kind: documentKind,
      purchaseOrder,
      requisition,
      receipt,
    });
  }, [applicationId, documentKind, documentName, documentNumber, purchaseOrder, receipt, requisition]);

  if (isLoading) {
    return (
      <>
        <RmPageHeader
          title={documentName}
          subtitle={documentNumber || "Loading document details..."}
          rightTopActions={(
            <DataRefreshAssistantCluster
              lastRefreshAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={canQuery ? async () => { await documentQuery.refetch(); } : undefined}
              disabled
            />
          )}
        />
        <RmBodyZone className="space-y-6 pb-10">
          <Skeleton className="h-[520px] w-full rounded-2xl border border-gray-100 bg-white shadow-sm" />
        </RmBodyZone>
      </>
    );
  }

  if (!documentData) {
    return (
      <>
        <RmPageHeader
          title="Document"
          subtitle="We couldn’t load this document. Refresh to try again."
          rightTopActions={(
            <DataRefreshAssistantCluster
              lastRefreshAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={canQuery ? async () => { await documentQuery.refetch(); } : undefined}
              assistantPromptOptions={assistantPrompt ? { prompt: assistantPrompt } : undefined}
              disabled={!assistantPrompt}
            />
          )}
        />
        <RmBodyZone className="space-y-6 pb-10">
          <Alert className="rounded-2xl border border-rose-100 bg-rose-50 shadow-sm">
            <AlertTriangle className="h-4 w-4 text-rose-600" aria-hidden="true" />
            <AlertTitle className="text-slate-900">Document not found</AlertTitle>
            <AlertDescription className="text-slate-700">
              The document you are looking for does not exist or you do not have permission to view it.
            </AlertDescription>
          </Alert>
        </RmBodyZone>
      </>
    );
  }

  const documentView = purchaseOrder ? (
    <PurchaseOrderView purchaseOrder={purchaseOrder} />
  ) : requisition ? (
    <RequisitionView requisition={requisition} />
  ) : receipt ? (
    <ReceiptView receipt={receipt} />
  ) : (
    <Alert className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
      <AlertTitle className="text-slate-900">Unsupported document type</AlertTitle>
      <AlertDescription className="text-slate-700">
        This document number format is not yet supported in the new Document view.
      </AlertDescription>
    </Alert>
  );

  const showHeaderActionsMenu = documentKind !== "purchase-order" && documentKind !== "reception";

  return (
    <>
      <RmPageHeader
        title={documentName}
        subtitle={documentNumber}
        rightTopActions={(
          <DataRefreshAssistantCluster
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isRefreshing}
            onRefresh={canQuery ? async () => { await documentQuery.refetch(); } : undefined}
            assistantPromptOptions={assistantPrompt ? { prompt: assistantPrompt } : undefined}
            disabled={!assistantPrompt}
          />
        )}
        rightBottomActions={showHeaderActionsMenu ? (
          <DocumentActionsMenu documentNumber={documentNumber} documentName={documentName} />
        ) : undefined}
      />
      <RmBodyZone className="space-y-8 pb-10" aria-busy={isRefreshing}>
        {documentView}
      </RmBodyZone>
    </>
  );
};

export default Document;
